import { isValid, parse } from "date-fns";
import { DateTime } from "luxon";

/**
 * Convertit une date au format "dd/MM/yyyy" (ex: "01/01/1970")
 * en chaîne ISO UTC (ex: "1970-01-01T00:00:00.000Z").
 */
export const toIsoUtcFromDate = (dateStr: string, format = "dd/MM/yyyy"): string => {
  return DateTime.fromFormat(dateStr, format, { zone: "utc" }).startOf("day").toISO()!;
};

/**
 * Formate une date ISO (ex: "1985-04-02T00:00:00.000Z")
 * en format français "dd/MM/yyyy" (ex: "02/04/1985").
 * @param isoDate - La date au format ISO
 * @param timezone - La timezone de sortie (par défaut: "Europe/Paris")
 */
export const formatIsoToDate = (isoDate: string, timezone = "Europe/Paris"): string => {
  const dt = DateTime.fromISO(isoDate, { zone: "utc" }).setZone(timezone);
  return dt.isValid ? dt.toFormat("dd/MM/yyyy") : "";
};

/**
 * Formate une date au format "yyyy-MM-dd" (ex: "1985-04-02")
 * en format français "dd/MM/yyyy" (ex: "02/04/1985").
 * Padde automatiquement si l'entrée n'est pas au format yyyy-MM-dd strict.
 */
export const formatDateToFr = (input: string | undefined): string => {
  const raw = input?.trim();
  if (!raw) return "-";

  if (!/^\d{4}-\d{1,2}-\d{1,2}$/.test(raw)) return "-";

  const dt = DateTime.fromFormat(raw, "yyyy-M-d", { zone: "utc" });
  return dt.isValid ? dt.toFormat("dd/MM/yyyy") : "-";
};

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}/;

/**
 * Parse une valeur de date saisie en une date locale
 * correspondant uniquement à la date civile (année / mois / jour).
 *
 * La fonction accepte :
 * - un format "YYYY-MM-DD" (ex: input type="date")
 * - un format ISO complet "YYYY-MM-DDTHH:mm:ss.sssZ"
 *
 * Dans tous les cas, seule la partie "YYYY-MM-DD" est prise en compte afin
 * d’éviter les effets de bord liés aux timezones (UTC).
 */
export const parseDateInputAsLocalDate = (val: string): Date | null => {
  const trimmed = val.trim();
  if (!trimmed) return null;

  const match = trimmed.match(DATE_ONLY_REGEX);
  if (!match) return null;

  const dateOnly = match[0];
  const date = parse(dateOnly, "yyyy-MM-dd", new Date());

  return isValid(date) ? date : null;
};
